import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Text from '../../../shared/Text';
import TypingAnimation from '../../../shared/TypingAnimation';
import { BOT } from '../utils/constants';

import { DotPulse } from '../../../lib/ui/animations';
import { getInitials } from '../utils/helpers';

export const Message = ({
  message,
  waitingBot = false,
  setBotIsTyping,
  botIsTyping,
  embed = false,
}) => {
  const { t } = useTranslation('hrAssistants');
  const { owner, text } = message;
  const fromBot = owner === BOT;
  const userName = useSelector(state => state.currentUser.name);
  const name = fromBot ? t('botName') : userName;

  const messageBody = () => {
    if (waitingBot) {
      return <DotPulse data-testid="hr-assistant-loading" />;
    }

    if (fromBot && botIsTyping) {
      return <TypingAnimation message={text} setIsTyping={setBotIsTyping} />;
    }

    return (
      <MessageBody isBot={fromBot} embed={embed} variant="subtitle2">
        <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
      </MessageBody>
    );
  };

  return (
    <MessageContainer embed={embed} isBot={fromBot}>
      <IconContainer embed={embed} isBot={fromBot}>
        {fromBot ? (
          <BotIcon />
        ) : (
          <Text variant="small1">{getInitials(name)}</Text>
        )}
      </IconContainer>
      <UserInfo>
        <Name embed={embed} isBot={fromBot} variant="subtitle2">
          {name}
        </Name>
        {messageBody()}
      </UserInfo>
    </MessageContainer>
  );
};

Message.propTypes = {
  message: PropTypes.object,
  waitingBot: PropTypes.bool,
  setBotIsTyping: PropTypes.func,
  botIsTyping: PropTypes.bool,
  embed: PropTypes.bool,
};

export const MessageBody = styled(Text)`
  p {
    margin: 0;

    ${props =>
      !props.isBot &&
      props.embed &&
      css`
        max-width: 300px;
      `}

    ${props =>
      !props.isBot &&
      !props.embed &&
      css`
        max-width: 500px;
      `}
  }
  ul {
    margin-bottom: 10px;
    list-style: disc;
  }
  ul li ul {
    list-style: circle;
    margin-left: 15px;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  gap: 12px;
  ${props =>
    !props.isBot &&
    css`
      flex-direction: row-reverse;
      margin-left: auto;
      width: fit-content;
      padding: 4px 8px;
      border-radius: 8px;
      background: ${props.theme.colors.primary[10]};
    `}
`;

const IconContainer = styled.div`
  height: 34px;
  width: 34px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background: ${props => (props.isBot ? '#66B2FF' : '#CCE5FF')};

  ${props =>
    !props.isBot &&
    css`
      display: none;
    `}
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Name = styled(Text)`
  font-weight: 600;
  ${props =>
    !props.isBot &&
    css`
      display: none;
    `}
`;

const BotIcon = styled.i.attrs({
  className: 'fa-light fa-sparkles fa-rotate-180',
})`
  font-size: 20px;
  font-weight: 500;
`;
